import { capitalize, FormControlLabel, Switch } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { BodyPart, Equipment, ExerciseForm } from '@strength/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../trpc/react';
import Badge, { defaultExerciseBadge } from '../../../components/badge/badge';
import { Gallery } from '../../../components/gallery/gallery';
import { useViewer } from '../../../context/viewer';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import { Tag } from '../tags/types';
import { CreateEditExerciseSheet } from './components/CreateEditExerciseSheet';
import { ExerciseCard } from './components/ExerciseCard';
import { createExerciseInput } from './components/exercises';
import { LibraryExercise, TrainingType } from './types';

const columnHelper = createColumnHelper<LibraryExercise>();

export function ExercisesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    org: {
      settings: { showBaseLibrary: orgShowBaseLibrary },
    },
    hasPermissions,
  } = useViewer();
  const teamId = useParams({
    from: '/orgAuth/orgWithNav/$teamId/library/exercises',
    select: (params: { teamId: string }) => params.teamId,
  });
  const enableCreate = hasPermissions('library.exercises', 'create');

  const { data: teamDetail } = trpc.roster.teamDetail.useQuery(undefined);

  const [includeBaseLibraryOverride, setIncludeBaseLibraryOverride] = useState<
    boolean | undefined
  >(undefined);
  const includeBaseLibrary =
    includeBaseLibraryOverride ??
    teamDetail?.settings.includeBaseLibraryExercises ??
    false;
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [types, setTypes] = useState<TrainingType[]>([]);
  const [selectedBodyParts, setSelectedBodyParts] = useState<BodyPart[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn: trpc.exerciseLibrary.exerciseSearch.useQuery,
    queryInput: {
      teamId: teamId || undefined,
      searchTerm: debouncedSearchTerm,
      trainingTypeIds: types.map((type) => type.id),
      bodyPartIds: selectedBodyParts.map((bp) => bp.id),
      equipmentIds: selectedEquipment.map((eq) => eq.id),
      tagIds: selectedTags.map((tag) => tag.id),
      includeBaseLibraryExercisesOverride: includeBaseLibrary,
    },
  });

  const { data: bodyParts, refetch: refetchBodyParts } =
    trpc.bodyPart.fetch.useQuery({});
  const { data: equipment, refetch: refetchEquipment } =
    trpc.equipment.fetch.useQuery({});
  const { data: tags, refetch: refetchTags } = trpc.tag.tags.useQuery({
    types: ['exercise'],
  });
  const { data: trainingTypes, refetch: refetchTrainingTypes } =
    trpc.trainingType.fetch.useQuery({});

  const utils = trpc.useUtils();

  const refreshExerciseFilters = () => {
    refetchBodyParts();
    refetchEquipment();
    refetchTags();
    refetchTrainingTypes();
  };

  // Also invalidate the block builder's preloaded library list so edits made
  // here show up there without waiting out its staleTime.
  const invalidateExerciseSearch = () =>
    utils.exerciseLibrary.exerciseSearch.invalidate();

  const createExercise = trpc.exerciseLibrary.createExercise.useMutation({
    onSuccess: () => {
      refetch();
      refreshExerciseFilters();
      invalidateExerciseSearch();
    },
  });

  const deleteExercise = trpc.exerciseLibrary.deleteExercise.useMutation({
    onSuccess: () => {
      refetch();
      invalidateExerciseSearch();
    },
  });

  const onDeleteExercise = (exercise: LibraryExercise) => {
    deleteExercise.mutate(
      { id: exercise.id },
      {
        onSuccess: () =>
          toast({
            title: t('exerciseDeleted', { name: exercise.name }),
            severity: 'success',
          }),
        onError: (_error) => {
          toast({
            title: t('errorDeletingExercise'),
            severity: 'error',
          });
        },
      },
    );
  };

  const onCreateExercise = ({ exercise }: { exercise: ExerciseForm }) => {
    createExercise.mutate(
      createExerciseInput(v4(), exercise, exercise.teamId || undefined),
      {
        onSuccess: () =>
          toast({
            title: t('exerciseCreated', { name: exercise.name }),
            severity: 'success',
          }),
        onError: (_error) =>
          toast({
            title: t('errorCreatingExercise'),
            severity: 'error',
          }),
      },
    );
  };

  const editExercise = trpc.exerciseLibrary.editExercise.useMutation({
    onSuccess: () => {
      refetch();
      refreshExerciseFilters();
      invalidateExerciseSearch();
    },
  });

  const onEditExercise = ({
    id,
    exercise,
  }: {
    id: string;
    exercise: ExerciseForm;
  }) => {
    editExercise.mutate(createExerciseInput(id, exercise), {
      onSuccess: () =>
        toast({
          title: t('exerciseEdited', { name: exercise.name }),
          severity: 'success',
        }),
      onError: (_error) =>
        toast({
          title: t('errorEditingExercise'),
          severity: 'error',
        }),
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('name'),
        cell: ({ row }) => {
          const { name, isDefault } = row.original;
          return (
            <div className="flex items-center gap-2">
              {capitalize(name)}
              {isDefault && <Badge {...defaultExerciseBadge(t)} />}
            </div>
          );
        },
      }),
      columnHelper.accessor('trainingType', {
        header: () => t('type'),
        cell: ({ row }) => row.original.trainingType.name,
      }),
      columnHelper.accessor('tags', {
        header: () => t('tags'),
        cell: ({ row }) =>
          row.original.tags.map((tag) => capitalize(tag.tag.name)).join(', '),
      }),
      ...(includeBaseLibrary
        ? [
            columnHelper.accessor('team', {
              header: () => t('team'),
              cell: ({ row }) =>
                row.original.team ? row.original.team.name : t('baseLibrary'),
            }),
          ]
        : []),
    ],
    [t, includeBaseLibrary],
  );

  // Debounce the search term that goes to the query
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  // Handle immediate UI updates and trigger debounced query update
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchTerm('');
    debouncedSetSearchTerm.clear(); // Clear any pending debounced calls
    setTypes([]);
    setSelectedTags([]);
    setSelectedBodyParts([]);
    setSelectedEquipment([]);
  }, [debouncedSetSearchTerm]);

  const filters = useMemo(
    () => [
      {
        id: 'trainingTypes',
        type: 'select' as const,
        title: t('exerciseType'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options:
          trainingTypes?.map((type) => ({
            id: type.id,
            label: type.name,
            value: type,
          })) ?? [],
      },
      {
        id: 'tags',
        type: 'select' as const,
        title: t('tags'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options:
          tags?.map((tag) => ({
            id: tag.id,
            label: tag.name,
            value: tag,
          })) ?? [],
      },
      {
        id: 'bodyParts',
        type: 'select' as const,
        title: t('bodyParts'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options:
          bodyParts?.map((bp) => ({
            id: bp.id,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: t(bp.name as any),
            value: bp,
          })) ?? [],
      },
      {
        id: 'equipment',
        type: 'select' as const,
        title: t('equipment'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options:
          equipment?.map((eq) => ({
            id: eq.id,
            label: eq.name,
            value: eq,
          })) ?? [],
      },
    ],
    [t, trainingTypes, tags, bodyParts, equipment],
  );

  const handleFilterChange = useCallback(
    (
      filters: Record<string, (TrainingType | Tag | BodyPart | Equipment)[]>,
    ) => {
      const trainingTypesFilter = filters['trainingTypes'];
      if (trainingTypesFilter) {
        setTypes(trainingTypesFilter as TrainingType[]);
      }

      const tagsFilter = filters['tags'];
      if (tagsFilter) {
        setSelectedTags(tagsFilter as Tag[]);
      }

      const bodyPartsFilter = filters['bodyParts'];
      if (bodyPartsFilter) {
        setSelectedBodyParts(bodyPartsFilter as BodyPart[]);
      }

      const equipmentFilter = filters['equipment'];
      if (equipmentFilter) {
        setSelectedEquipment(equipmentFilter as Equipment[]);
      }
    },
    [],
  );

  return (
    <Gallery<LibraryExercise>
      title={t('exercises')}
      createButtonText={t('createExercise')}
      createDisabled={!enableCreate}
      createDisabledReason={t('permissionDisabled.create', {
        item: t('exercises'),
      })}
      data={data}
      columns={columns}
      filters={filters}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      headerActions={
        orgShowBaseLibrary ? (
          <FormControlLabel
            control={
              <Switch
                checked={includeBaseLibrary}
                onChange={(e) =>
                  setIncludeBaseLibraryOverride(e.target.checked)
                }
                size="small"
                inputProps={{
                  'aria-label': includeBaseLibrary
                    ? t('hideDefault')
                    : t('showDefault'),
                }}
              />
            }
            label={t('includeBaseLibrary')}
            sx={{ gap: 1 }}
          />
        ) : undefined
      }
      renderCard={(item, viewMode, setSelected) => (
        <ExerciseCard
          key={item.id}
          item={item}
          viewMode={viewMode}
          onClick={setSelected}
        />
      )}
      renderEditSheet={(selected, setSelected) => (
        <CreateEditExerciseSheet
          exercise={selected}
          open={!!selected}
          onOpenChange={() => setSelected(null)}
          onSubmit={onEditExercise}
          onDelete={onDeleteExercise}
        />
      )}
      renderCreateSheet={
        enableCreate
          ? (isOpen, setIsOpen) => (
              <CreateEditExerciseSheet
                key={teamId}
                open={isOpen}
                onOpenChange={() => setIsOpen(false)}
                onSubmit={onCreateExercise}
                teamId={teamId || undefined}
              />
            )
          : undefined
      }
    />
  );
}
